// Keeps Shopify's own floating widgets — the Inbox chat bubble
// (<shopify-chat>, bottom-right) and the Forms email-capture teaser
// (<shopify-forms-embed>, bottom-left) — from sitting on top of our
// Build-a-Box sticky tray (.bab-bar) and hiding its contents.
//
// The chat bubble additionally gets a UNIFORM position on every page,
// tray or no tray (e.g. the feeding calculator has no tray, but the
// bubble should still sit at the same height it does on pages that do,
// not fall back to Shopify's raw corner default). The Forms teaser only
// gets moved when there's an actual tray to clear — it has no reason to
// deviate from Shopify's own default position elsewhere.
//
// Both widgets render inside an open shadow root, and both hardcode their
// own bottom offset in their shadow stylesheet rather than reading it from
// a custom property (verified directly for both), so this reaches into
// each shadow root and sets the real element's inline `bottom` instead.
//
// <shopify-chat> in particular replaces its own internal markup the first
// time a visitor opens the chat panel: the plain `.activator-nudge` in its
// shadow root is torn down and a nested <shopify-agent-runtime> custom
// element (with its own separate shadow root) takes over, containing a
// *different* `.activator-nudge` node. Caching "the" nudge across calls
// goes stale the moment that swap happens — open the chat once, minimize
// it, and the bubble silently reverts to Shopify's unstyled default
// (both position and size) because we kept styling a detached element.
// findChatNudge() below always re-resolves the current node instead of
// trusting a cached reference, and reposition() re-applies size + position
// whenever the resolved node is a different element than last time.
//
// Loaded globally (layout/theme.liquid) rather than from the Build-a-Box
// island itself, since the chat bubble's "uniform everywhere" requirement
// means this has to run on pages (like the calculator) that never load
// that island.
(function () {
  var GAP = 12;
  var CHAT_SCALE = 0.75;
  var FORMS_SCALE = 0.85;
  // Matches the collapsed tray's own rendered height + GAP, so pages with
  // no tray at all still park the chat bubble at the same height as pages
  // that do.
  var BASELINE_BOTTOM = 70;

  var styledChatNudge = null;
  var styledFormsTeaser = null;
  var observedBar = null;
  var observedChatShadow = null;
  var observedRuntimeShadow = null;

  function findChatNudge() {
    var host = document.querySelector('shopify-chat');
    if (!host || !host.shadowRoot) return null;

    var direct = host.shadowRoot.querySelector('.activator-nudge');
    if (direct && direct.isConnected) return direct;

    // Post-interaction state: the widget mounts a nested
    // <shopify-agent-runtime> with its own shadow root and its own
    // `.activator-nudge`, distinct from the one above.
    var runtime = host.shadowRoot.querySelector('shopify-agent-runtime');
    var nested = runtime && runtime.shadowRoot && runtime.shadowRoot.querySelector('.activator-nudge');
    return (nested && nested.isConnected) ? nested : null;
  }

  function findFormsTeaser() {
    var host = document.querySelector('shopify-forms-embed');
    var el = host && host.shadowRoot && host.shadowRoot.querySelector('.appEmbed');
    return (el && el.isConnected) ? el : null;
  }

  function barHeight() {
    var bar = document.querySelector('.bab-bar');
    return bar ? bar.getBoundingClientRect().height : 0;
  }

  // Any mutation inside <shopify-chat>'s own shadow tree (or its nested
  // <shopify-agent-runtime> shadow tree, once that appears) re-runs
  // reposition() immediately — these are shadow-internal changes that a
  // light-DOM (document.body) observer can never see, and are exactly
  // when the widget swaps in the node this script needs to re-find.
  function watchChatShadowTrees() {
    var host = document.querySelector('shopify-chat');
    if (host && host.shadowRoot && host.shadowRoot !== observedChatShadow) {
      observedChatShadow = host.shadowRoot;
      new MutationObserver(reposition).observe(observedChatShadow, { childList: true, subtree: true });
    }
    var runtime = host && host.shadowRoot && host.shadowRoot.querySelector('shopify-agent-runtime');
    if (runtime && runtime.shadowRoot && runtime.shadowRoot !== observedRuntimeShadow) {
      observedRuntimeShadow = runtime.shadowRoot;
      new MutationObserver(reposition).observe(observedRuntimeShadow, { childList: true, subtree: true });
    }
  }

  function reposition() {
    var height = barHeight();
    var bottom = (height > 0 ? height + GAP : BASELINE_BOTTOM) + 'px';

    var chatNudge = findChatNudge();
    if (chatNudge) {
      if (chatNudge !== styledChatNudge) {
        chatNudge.style.transformOrigin = 'bottom right';
        chatNudge.style.transform = 'scale(' + CHAT_SCALE + ')';
        styledChatNudge = chatNudge;
      }
      chatNudge.style.bottom = bottom;
    }
    watchChatShadowTrees();

    // The Forms teaser only needs to move when there's an actual tray to
    // clear — no reason to touch it on pages (like the calculator) that
    // never show one.
    if (height > 0) {
      var formsTeaser = findFormsTeaser();
      if (formsTeaser) {
        if (formsTeaser !== styledFormsTeaser) {
          formsTeaser.style.transformOrigin = 'bottom left';
          formsTeaser.style.transform = 'scale(' + FORMS_SCALE + ')';
          styledFormsTeaser = formsTeaser;
        }
        formsTeaser.style.bottom = bottom;
      }
    }

    var bar = document.querySelector('.bab-bar');
    if (bar && bar !== observedBar && window.ResizeObserver) {
      observedBar = bar;
      new ResizeObserver(reposition).observe(bar);
    }
  }

  window.woofingRepositionFloatingWidgets = reposition;

  reposition();
  // <shopify-chat>, <shopify-forms-embed> and our own .bab-bar can all
  // mount asynchronously and at different times, so re-check whenever the
  // page's shape changes rather than guessing a fixed delay.
  new MutationObserver(reposition).observe(document.body, { childList: true, subtree: true });

  // Belt-and-suspenders for the initial attach: both widgets populate their
  // *own* shadow root well after their custom element tag first appears in
  // the light DOM, with nothing else visible to the light DOM signaling
  // when that's done — on a page with no Build-a-Box island driving other
  // DOM churn (e.g. the feeding calculator), that can leave the body-level
  // observer above with nothing to fire on afterward, silently stranding
  // the widget unrepositioned. A short poll guarantees we eventually catch
  // it regardless of how/when either widget finishes its own hydration.
  // Stops once the chat bubble (the widget that must be uniform on every
  // page) is attached; the body-level observer above stays active for the
  // rest of the page's life as an ongoing fallback for the Forms teaser,
  // which only matters on tray pages — those have plenty of their own DOM
  // churn (the Build-a-Box island rendering) to keep retrying against.
  var attempts = 0;
  var poll = setInterval(function () {
    attempts++;
    reposition();
    if (styledChatNudge || attempts >= 20) clearInterval(poll);
  }, 500);
})();
